import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "../api";
import { dateTime } from "../format";
import { useOrg } from "../org";
import { useSession, ROLE_LABELS, type Role } from "../session";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Notice, PageHeader, Select, SimBadge, Table, fieldError, useToast } from "../ui";

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as Role[]).map((r) => ({ value: r, label: ROLE_LABELS[r] }));

export function MembersPage() {
  const org = useOrg();
  const { user } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const members = useQuery({ queryKey: [org.api("/members")], queryFn: () => get(org.api("/members")) });
  const invitations = useQuery({ queryKey: [org.api("/invitations")], queryFn: () => get(org.api("/invitations")), enabled: org.can("members") });
  const [form, setForm] = useState({ email: "", role: "collaborateur" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: [org.api("/members")] });
    qc.invalidateQueries({ queryKey: [org.api("/invitations")] });
  };
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Membres et droits" intro="Chaque membre voit uniquement cet espace. Les droits sont vérifiés par le serveur à chaque action." />
      <Card title="Membres">
        {members.isLoading ? <Loading /> : null}
        <Table caption="Membres de l’espace">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Adresse</th>
              <th>Rôle</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(members.data?.items ?? []).map((m: any) => (
              <tr key={m.user_id}>
                <td>{m.full_name}</td>
                <td>{m.email}</td>
                <td>
                  {org.can("members") && m.user_id !== user?.id ? (
                    <select
                      aria-label={`Rôle de ${m.full_name}`}
                      value={m.role}
                      onChange={async (e) => {
                        try {
                          await patch(org.api(`/members/${m.user_id}`), { role: e.target.value });
                          toast("Rôle modifié.");
                          refresh();
                        } catch (err: any) {
                          toast(err.message, "erreur");
                        }
                      }}
                    >
                      {ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    ROLE_LABELS[m.role as Role]
                  )}
                </td>
                <td className="right">
                  {org.can("members") && m.user_id !== user?.id ? (
                    <Button
                      variant="discret"
                      small
                      onClick={async () => {
                        try {
                          await del(org.api(`/members/${m.user_id}`));
                          toast("Membre retiré.");
                          refresh();
                        } catch (err: any) {
                          toast(err.message, "erreur");
                        }
                      }}
                    >
                      Retirer
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      {org.can("members") ? (
        <Card title="Inviter une personne">
          <form
            className="form-row"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              try {
                await post(org.api("/invitations"), form);
                toast("Invitation envoyée.");
                setForm({ ...form, email: "" });
                refresh();
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Input label="Adresse e-mail" type="email" required value={form.email} error={fieldError(error, "email")} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Select label="Rôle" value={form.role} options={ROLE_OPTIONS} onChange={(e) => setForm({ ...form, role: e.target.value })} />
            <Button type="submit" busy={busy}>
              Inviter
            </Button>
          </form>
          <ErrorBox error={error && !fieldError(error, "email") ? error : null} />
          {(invitations.data?.items ?? []).length ? (
            <Table caption="Invitations">
              <thead>
                <tr>
                  <th>Adresse</th>
                  <th>Rôle</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invitations.data.items.map((i: any) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{ROLE_LABELS[i.role as Role]}</td>
                    <td>{i.accepted_at ? <Badge tone="succes">Acceptée</Badge> : i.revoked_at ? <Badge>Annulée</Badge> : new Date(i.expires_at) < new Date() ? <Badge>Expirée</Badge> : <Badge tone="encre">En attente</Badge>}</td>
                    <td className="right">
                      {!i.accepted_at && !i.revoked_at ? (
                        <Button
                          variant="discret"
                          small
                          onClick={async () => {
                            await post(org.api(`/invitations/${i.id}/revoke`));
                            refresh();
                          }}
                        >
                          Annuler
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>
      ) : null}
      <Card title="Ce que permet chaque rôle">
        <ul>
          <li>
            <strong>Propriétaire</strong> : tout, y compris l’abonnement et les membres.
          </li>
          <li>
            <strong>Gestionnaire</strong> : tout sauf l’abonnement ; peut retenir ou refuser un candidat.
          </li>
          <li>
            <strong>Collaborateur</strong> : logements, campagnes, baux, messages ; pas de décision sur un candidat ni de finances.
          </li>
          <li>
            <strong>Comptable</strong> : lecture et opérations financières.
          </li>
          <li>
            <strong>Lecture seule</strong> : consultation.
          </li>
        </ul>
      </Card>
    </div>
  );
}

export function OutboxPage() {
  const org = useOrg();
  const q = useQuery({ queryKey: [org.api("/outbox")], queryFn: () => get(org.api("/outbox")) });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Envois" intro="Courriels, SMS et courriers émis par Bailivo pour cet espace." />
      <Notice tone="simulateur" title="Envois simulés">
        Aucun prestataire d’envoi n’est encore branché : les messages ci-dessous n’ont pas quitté Bailivo. Ils montrent exactement ce qui serait envoyé.
      </Notice>
      {q.isLoading ? <Loading /> : null}
      {q.data?.items?.length === 0 ? <Empty title="Aucun envoi pour le moment." /> : null}
      <div className="stack">
        {(q.data?.items ?? []).map((n: any) => (
          <Card key={n.id}>
            <div className="row-between">
              <div>
                <strong>{n.subject || n.template}</strong>
                <p className="small muted">
                  {n.channel} → {n.to_address} · {dateTime(n.created_at)}
                </p>
              </div>
              <div className="row">
                <SimBadge mode={n.status === "simule" ? "simulateur" : "production"} />
                <Button variant="discret" small onClick={() => setOpen(open === n.id ? null : n.id)}>
                  {open === n.id ? "Masquer" : "Lire"}
                </Button>
              </div>
            </div>
            {open === n.id ? <pre className="message-body">{n.body}</pre> : null}
          </Card>
        ))}
      </div>
    </div>
  );
}

export function ServicesPage() {
  const org = useOrg();
  const adapters = useQuery({ queryKey: ["adapters"], queryFn: () => get("/api/v1/system/adapters") });
  const calls = useQuery({ queryKey: [org.api("/integration-calls")], queryFn: () => get(org.api("/integration-calls")) });
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Services tiers" intro="Mode de chaque service et historique des appels. Un résultat de simulateur n’est jamais une vérification réelle." />
      <Card title="Services configurés">
        <ErrorBox error={adapters.error} />
        <Table caption="Services">
          <thead>
            <tr>
              <th>Service</th>
              <th>Fournisseur</th>
              <th>Module</th>
              <th>Mode</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(adapters.data ?? {}).map(([k, v]: [string, any]) => (
              <tr key={k}>
                <td>{v.libelle ?? k.replace(/_/g, " ")}</td>
                <td>{v.nom}</td>
                <td>{v.module ?? "socle"}</td>
                <td>{v.mode === "production" ? <Badge tone="succes">Réel</Badge> : <SimBadge mode={v.mode === "sandbox" ? "sandbox" : "simulateur"} />}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card title="Derniers appels">
        {calls.isLoading ? <Loading /> : null}
        {calls.data?.items?.length === 0 ? <Empty title="Aucun appel enregistré." /> : null}
        {(calls.data?.items ?? []).length ? (
          <Table caption="Appels">
            <thead>
              <tr>
                <th>Date</th>
                <th>Service</th>
                <th>Opération</th>
                <th>Finalité</th>
                <th>Mode</th>
                <th>Résultat</th>
              </tr>
            </thead>
            <tbody>
              {calls.data.items.map((c: any) => (
                <tr key={c.id}>
                  <td>{dateTime(c.created_at)}</td>
                  <td>{c.adapter}</td>
                  <td>{c.operation}</td>
                  <td>{c.purpose}</td>
                  <td>{c.mode}</td>
                  <td>{c.status === "ok" ? "OK" : <Badge tone="erreur">Erreur</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}

export function AuditPage() {
  const org = useOrg();
  const q = useQuery({ queryKey: [org.api("/audit")], queryFn: () => get(org.api("/audit")) });
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Journal" intro="Actions sensibles : décisions, accès aux pièces, droits, suppressions." />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      <Table caption="Journal">
        <thead>
          <tr>
            <th>Date</th>
            <th>Personne</th>
            <th>Action</th>
            <th>Élément</th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.items ?? []).map((a: any) => (
            <tr key={a.id}>
              <td>{dateTime(a.created_at)}</td>
              <td>{a.user_name ?? "—"}</td>
              <td>
                <code>{a.action}</code>
              </td>
              <td className="small muted">{a.subject_type ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
